// LLM generated - Model Claude 4 Sonnet
import React from 'react';
import { useMantineTheme } from '@mantine/core';
import { IconExclamationMark } from '@tabler/icons-react';
import { match } from 'ts-pattern';

import { Text } from '~/domains/design-system/Text';
import { SERVICE_FEE_ALERT_STATUSES } from '~/domains/contest/domains/common/constants';
import type { ServiceFeeAlertStatusType } from '~/domains/contest/domains/common/constants';

import useStyles from '~/domains/manage-contest/components/ContestAlert/ContestAlert.styles';

export interface ContestAlertProps {
  status: ServiceFeeAlertStatusType;
  message: string;
  variant: 'icon' | 'tag' | 'banner';
  className?: string;
}

export function ContestAlert({ status, message, variant, className }: ContestAlertProps) {
  const { classes, cx } = useStyles();
  const theme = useMantineTheme();

  // Get status-based styling class
  const getStatusClass = (alertStatus: ServiceFeeAlertStatusType): string =>
    match(alertStatus)
      .with(SERVICE_FEE_ALERT_STATUSES.ACTION_REQUIRED, () => classes.actionRequired)
      .with(SERVICE_FEE_ALERT_STATUSES.WARNING, () => classes.warning)
      .with(SERVICE_FEE_ALERT_STATUSES.INFO, () => classes.info)
      .exhaustive();

  // Get display content based on variant
  const getDisplayContent = (): React.ReactNode => {
    if (variant === 'icon') {
      const iconColor =
        status === SERVICE_FEE_ALERT_STATUSES.WARNING || status === SERVICE_FEE_ALERT_STATUSES.INFO
          ? theme.black
          : theme.white;
      return <IconExclamationMark size={12} stroke={2.5} color={iconColor} />;
    }
    // For tag variant, use the message (which can be short or long based on context)
    return message;
  };

  // Get variant-specific base class
  const getVariantBaseClass = (): string =>
    match(variant)
      .with('icon', () => classes.iconBase)
      .with('banner', () => classes.bannerBase)
      .with('tag', () => classes.tagBase)
      .exhaustive();

  // Get text color class based on status
  const getTextClass = (): string =>
    status === SERVICE_FEE_ALERT_STATUSES.WARNING || status === SERVICE_FEE_ALERT_STATUSES.INFO
      ? cx(classes.alertText, classes.altText)
      : classes.alertText; // ACTION_REQUIRED uses default white text

  // Get ARIA role based on status
  const getAriaRole = (): string =>
    match(status)
      .with(SERVICE_FEE_ALERT_STATUSES.ACTION_REQUIRED, () => 'alert')
      .with(SERVICE_FEE_ALERT_STATUSES.WARNING, () => 'status')
      .with(SERVICE_FEE_ALERT_STATUSES.INFO, () => 'status')
      .exhaustive();

  // Get ARIA label for accessibility
  const getAriaLabel = (): string => {
    const statusText = match(status)
      .with(SERVICE_FEE_ALERT_STATUSES.ACTION_REQUIRED, () => 'Action required')
      .with(SERVICE_FEE_ALERT_STATUSES.WARNING, () => 'Warning')
      .with(SERVICE_FEE_ALERT_STATUSES.INFO, () => 'Information')
      .exhaustive();

    return variant === 'icon' ? `${statusText}: ${message}` : statusText;
  };

  return (
    <div
      className={cx(classes.baseShared, getVariantBaseClass(), getStatusClass(status), className)}
      role={getAriaRole()}
      aria-label={getAriaLabel()}
    >
      {variant === 'icon' ? (
        getDisplayContent()
      ) : (
        <Text variant="subhead-small" className={getTextClass()}>
          {getDisplayContent()}
        </Text>
      )}
    </div>
  );
}

ContestAlert.displayName = 'ContestAlert';
